import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, AlertTriangle } from 'lucide-react';
import { ListSection } from '../components/ios';

// iOS inset-grouped content row inside a ListSection card.
function Block({ title, children }) {
  return (
    <div className="px-4 py-3">
      {title && <h4 className="font-semibold text-gray-900 mb-2 text-[15px]">{title}</h4>}
      {children}
    </div>
  );
}

function BulletList({ items, bulletClass = 'text-[#8E8E93]' }) {
  return (
    <ul className="space-y-2">
      {items.map((item, i) => (
        <li key={i} className="flex items-start">
          <span className={`${bulletClass} mr-2`}>•</span>
          <span className="text-[15px] text-gray-700">{item}</span>
        </li>
      ))}
    </ul>
  );
}

function LiabilityDisclaimerPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#F2F2F7]">
      {/* iOS inline navigation bar: back chevron + centered title */}
      <header className="sticky top-0 z-40 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
        <div className="relative flex items-center justify-between px-4 h-12 max-w-lg mx-auto">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex items-center -ml-2 text-[#FF3B30] active:opacity-60"
            aria-label="Back"
          >
            <ChevronLeft size={24} />
          </button>
          <h1 className="absolute left-1/2 -translate-x-1/2 text-[16px] font-semibold text-gray-900">
            Liability Waiver
          </h1>
          <span className="w-6" />
        </div>
      </header>

      <div className="max-w-lg mx-auto pb-8">
        <p className="px-8 mt-4 text-[13px] text-[#6D6D72]">Last Updated: November 3, 2024</p>

        {/* Important Notice */}
        <div className="px-4 mt-4">
          <div className="bg-[#FF3B30]/10 rounded-xl px-4 py-3 flex items-start gap-3">
            <AlertTriangle size={20} className="text-[#FF3B30] shrink-0 mt-0.5" />
            <div>
              <h3 className="text-[15px] font-semibold text-[#FF3B30] mb-1">Important Notice</h3>
              <p className="text-[13px] text-[#FF3B30] font-medium leading-relaxed">
                PLEASE READ THIS DISCLAIMER CAREFULLY BEFORE USING THE CheF APP. BY USING THIS APP, YOU ACKNOWLEDGE AND AGREE TO THE TERMS SET FORTH BELOW.
              </p>
            </div>
          </div>
        </div>

        <ListSection title="1. Platform Service Only">
          <Block>
            <p className="text-[15px] text-gray-700 mb-2">
              Browne Ink operates the CheF App solely as a technology platform that connects:
            </p>
            <BulletList items={[
              'Customers seeking food delivery services',
              'Restaurants preparing and selling food',
              'Independent delivery drivers',
            ]} />
          </Block>
          <Block>
            <p className="font-semibold text-[#FF3B30] mb-2 text-[15px]">We DO NOT:</p>
            <BulletList bulletClass="text-[#FF3B30]" items={[
              'Prepare, cook, or handle food',
              'Employ restaurants or drivers',
              'Control food preparation methods',
              'Guarantee food quality or safety',
              'Operate as a restaurant or food service provider',
            ]} />
          </Block>
        </ListSection>

        <ListSection title="2. Restaurant Responsibility">
          <Block title="2.1 Food Safety and Quality">
            <p className="text-[15px] text-gray-700 mb-2">Restaurants are SOLELY RESPONSIBLE for:</p>
            <BulletList items={[
              'Food preparation, handling, and storage',
              'Compliance with all food safety regulations',
              'Ingredient quality and freshness',
              'Proper cooking temperatures and methods',
              'Allergen information and warnings',
              'Nutritional accuracy',
              'Food packaging and temperature maintenance',
            ]} />
          </Block>
          <Block title="2.2 Health and Safety Compliance">
            <p className="text-[15px] text-gray-700 mb-2">Restaurants must:</p>
            <BulletList items={[
              'Maintain all required health permits and licenses',
              'Follow local health department regulations',
              'Implement proper food safety protocols',
              'Train staff in food handling procedures',
            ]} />
          </Block>
        </ListSection>

        <ListSection title="3. Driver Independence">
          <Block>
            <p className="text-[15px] text-gray-700 mb-2">
              Delivery drivers are independent contractors, NOT employees of Browne Ink. Drivers are responsible for:
            </p>
            <BulletList items={[
              'Their own vehicle maintenance and insurance',
              'Safe driving practices',
              'Timely delivery of orders',
              'Proper handling of food during transport',
              'Compliance with traffic laws',
            ]} />
          </Block>
        </ListSection>

        <ListSection title="4. Limitation of Liability">
          <Block title="4.1 Food-Related Issues">
            <p className="text-[15px] text-gray-700 mb-2">Browne Ink is NOT LIABLE for:</p>
            <BulletList bulletClass="text-[#FF3B30]" items={[
              'Food poisoning or foodborne illness',
              'Allergic reactions',
              'Food quality, taste, or temperature',
              'Incorrect ingredients or preparation',
              'Foreign objects in food',
              'Expired or spoiled ingredients',
              'Cross-contamination',
            ]} />
          </Block>
          <Block title="4.2 Delivery Issues">
            <p className="text-[15px] text-gray-700 mb-2">Browne Ink is NOT LIABLE for:</p>
            <BulletList bulletClass="text-[#FF9500]" items={[
              'Delivery delays or late arrivals',
              'Driver accidents or incidents',
              'Lost or stolen orders',
              'Incorrect delivery addresses provided by customers',
              'Damage to food during transport',
            ]} />
          </Block>
          <Block title="4.3 General Limitations">
            <p className="text-[15px] text-gray-700 mb-2">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, BROWNE INK SHALL NOT BE LIABLE FOR:
            </p>
            <BulletList items={[
              'Any indirect, incidental, special, or consequential damages',
              'Loss of profits or business opportunities',
              'Data loss or system downtime',
              'Third-party service interruptions',
            ]} />
          </Block>
        </ListSection>

        <ListSection title="Contact Us">
          <Block>
            <p className="text-[15px] text-gray-700">
              If you have questions about this Liability Disclaimer, please contact us at:
            </p>
            <div className="mt-2 space-y-1">
              <p className="text-[15px] text-gray-700"><strong>Email:</strong> browneinc7@gmail.com</p>
              <p className="text-[15px] text-gray-700"><strong>Phone:</strong> 1 869 660 1052</p>
            </div>
          </Block>
        </ListSection>
      </div>
    </div>
  );
}

export default LiabilityDisclaimerPage;
